import React, { useState } from 'react';

const MONTOS_RAPIDOS = [100, 200, 500, 1000];

function formatColones(valor: number): string {
  return `₡${valor.toLocaleString('es-CR')}`;
}

interface AmountSelectorProps {
  label: string;
  value: number;
  isCustom: boolean;
  customInput: string;
  color: 'blue' | 'amber';
  showZero?: boolean;
  onChange: (value: number, isCustom: boolean, customInput: string) => void;
}

export const AmountSelector: React.FC<AmountSelectorProps> = ({
  label,
  value,
  isCustom,
  customInput,
  color,
  showZero = false,
  onChange,
}) => {
  const [showCustomInput, setShowCustomInput] = useState(isCustom);

  const colorClasses = color === 'blue'
    ? { active: 'bg-blue-600 border-blue-500 text-white', ring: 'border-blue-500' }
    : { active: 'bg-blue-600 border-blue-500 text-white', ring: 'border-blue-500' };

  const amounts = showZero ? [0, ...MONTOS_RAPIDOS] : MONTOS_RAPIDOS;

  const handleQuickSelect = (m: number) => {
    setShowCustomInput(false);
    onChange(m, false, '');
  };

  const handleOtroClick = () => {
    const next = !showCustomInput;
    setShowCustomInput(next);
    if (next) {
      onChange(0, true, '');
    } else {
      onChange(200, false, '');
    }
  };

  const handleCustomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.replace(/\D/g, '');
    onChange(Number(v) || 0, true, v);
  };

  const effectiveValue = isCustom ? Number(customInput) || 0 : value;

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-400 uppercase tracking-wide font-bold">{label}</p>
      <div className="flex flex-wrap gap-2">
        {amounts.map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => handleQuickSelect(m)}
            className={`flex-1 min-w-[60px] py-3 rounded-xl font-bold text-sm border transition-all ${
              !isCustom && value === m
                ? colorClasses.active
                : 'bg-gray-800/80 border-gray-700 text-white hover:border-gray-500'
            }`}
          >
            {m === 0 ? '₡0' : formatColones(m)}
          </button>
        ))}
        <button
          type="button"
          onClick={handleOtroClick}
          className={`flex-1 min-w-[60px] py-3 rounded-xl font-bold text-sm border transition-all ${
            showCustomInput
              ? colorClasses.active
              : 'bg-gray-800/80 border-gray-700 text-gray-300 hover:border-gray-500'
          }`}
        >
          Otro
        </button>
      </div>
      {showCustomInput && (
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">₡</span>
          <input
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            value={customInput}
            onChange={handleCustomChange}
            className={`w-full bg-gray-800 text-white text-2xl font-mono rounded-xl pl-10 pr-4 py-3 border-2 ${colorClasses.ring} focus:outline-none`}
            placeholder="0"
            autoFocus
          />
          <p className="text-xs text-gray-500 mt-1">Min. ₡100</p>
        </div>
      )}
    </div>
  );
};
